import React, { useState, useEffect } from 'react';
import { WifiOff, Wifi, X, RefreshCw, CheckCircle2 } from 'lucide-react';
import { getPendingCount } from '../services/offlineSync';

export const OfflineBanner: React.FC = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showReconnected, setShowReconnected] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [pendingCount, setPendingCount] = useState(getPendingCount());
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ success: number; failed: number } | null>(null);

  useEffect(() => {
    const goOffline = () => {
      setIsOffline(true);
      setDismissed(false);
      setShowReconnected(false);
      setSyncResult(null);
      setPendingCount(getPendingCount());
    };
    const goOnline = () => {
      setIsOffline(false);
      setShowReconnected(true);
      setPendingCount(getPendingCount());
      setTimeout(() => setShowReconnected(false), 5000);
    };
    const onQueueChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setPendingCount(detail?.count ?? getPendingCount());
    };
    const onSyncStart = () => {
      setIsSyncing(true);
      setSyncResult(null);
    };
    const onSyncComplete = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setIsSyncing(false);
      setSyncResult(detail);
      setPendingCount(getPendingCount());
      setTimeout(() => setSyncResult(null), 4000);
    };

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    window.addEventListener('offlineQueueChange', onQueueChange);
    window.addEventListener('offlineSyncStart', onSyncStart);
    window.addEventListener('offlineSyncComplete', onSyncComplete);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('offlineQueueChange', onQueueChange);
      window.removeEventListener('offlineSyncStart', onSyncStart);
      window.removeEventListener('offlineSyncComplete', onSyncComplete);
    };
  }, []);

  const showBanner = isOffline || showReconnected || isSyncing || syncResult;
  if (dismissed && isOffline && pendingCount === 0) return null;
  if (!showBanner) return null;

  return (
    <div
      data-testid="offline-banner"
      className={`fixed top-0 left-0 right-0 z-[200] px-4 py-2.5 flex items-center justify-center gap-2 text-sm font-bold transition-all duration-300 ${
        isOffline
          ? 'bg-red-600 text-white'
          : isSyncing
            ? 'bg-amber-500 text-white'
            : syncResult
              ? syncResult.failed > 0 ? 'bg-amber-500 text-white' : 'bg-green-600 text-white'
              : 'bg-green-600 text-white'
      }`}
    >
      {isOffline ? (
        <>
          <WifiOff size={16} />
          <span>ऑफ़लाइन — डेटा locally save हो रहा है{pendingCount > 0 ? ` (${pendingCount} pending)` : ''}</span>
          <button onClick={() => setDismissed(true)} className="ml-2 p-0.5 hover:bg-white/20 rounded">
            <X size={14} />
          </button>
        </>
      ) : isSyncing ? (
        <>
          <RefreshCw size={16} className="animate-spin" />
          <span>सिंक हो रहा है...</span>
        </>
      ) : syncResult ? (
        <>
          <CheckCircle2 size={16} />
          <span>
            {syncResult.failed > 0
              ? `${syncResult.success} सिंक, ${syncResult.failed} pending`
              : `सब सिंक हो गया!`}
          </span>
        </>
      ) : (
        <>
          <Wifi size={16} />
          <span>वापस ऑनलाइन आ गए!</span>
        </>
      )}
    </div>
  );
};
